import { useCallback, useEffect, useState } from 'react';

export interface GeolocationState {
	latitude: number | null;
	longitude: number | null;
	/** Horizontal accuracy of the fix in metres (95% radius), when the device reports one. */
	accuracy: number | null;
	/** ISO-8601 time the fix was taken, from the position's own timestamp. */
	capturedAt: string | null;
	loading: boolean;
	error: string | null;
}

export interface UseGeolocationReturn extends GeolocationState {
	retry: () => void;
}

const NO_FIX = { latitude: null, longitude: null, accuracy: null, capturedAt: null } as const;

const getErrorMessage = (error: GeolocationPositionError): string => {
	switch (error.code) {
		case error.PERMISSION_DENIED:
			return 'Location access denied. Please allow location access in your browser settings to proceed.';
		case error.POSITION_UNAVAILABLE:
			return 'Location information is unavailable. Please ensure your device has location services enabled.';
		case error.TIMEOUT:
			return 'Location request timed out. Please try again.';
		default:
			return 'An unknown error occurred while getting your location.';
	}
};

const toIsoTimestamp = (timestamp: number | undefined): string | null => {
	if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) return null;
	try {
		return new Date(timestamp).toISOString();
	} catch {
		return null;
	}
};

const toAccuracy = (accuracy: number | null | undefined): number | null =>
	typeof accuracy === 'number' && Number.isFinite(accuracy) && accuracy >= 0 ? accuracy : null;

/**
 * Custom hook for browser geolocation API.
 * Requests location automatically on mount and provides state and retry functionality.
 *
 * Call it ONCE per screen: every mount is a fresh permission request on
 * browsers that have not yet remembered the decision. Components that only
 * need the fix should read it from wherever the single caller stored it.
 *
 * @example
 * ```tsx
 * const { latitude, longitude, accuracy, loading, error, retry } = useGeolocation();
 *
 * if (loading) return <p>Requesting location access...</p>;
 * if (error) return <p>{error} <button onClick={retry}>Try Again</button></p>;
 * if (latitude && longitude) return <p>Location: {latitude}, {longitude}</p>;
 * ```
 */
export const useGeolocation = (): UseGeolocationReturn => {
	const [state, setState] = useState<GeolocationState>({
		...NO_FIX,
		loading: true,
		error: null,
	});

	const requestLocation = useCallback(() => {
		// Check if geolocation is supported
		if (!navigator.geolocation) {
			setState({
				...NO_FIX,
				loading: false,
				error: 'Geolocation is not supported by your browser.',
			});
			return;
		}

		// Reset state and start loading
		setState((prev) => ({
			...prev,
			loading: true,
			error: null,
		}));

		navigator.geolocation.getCurrentPosition(
			(position) => {
				setState({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
					accuracy: toAccuracy(position.coords.accuracy),
					capturedAt: toIsoTimestamp(position.timestamp),
					loading: false,
					error: null,
				});
			},
			(error) => {
				setState({
					...NO_FIX,
					loading: false,
					error: getErrorMessage(error),
				});
			},
			{
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 0,
			}
		);
	}, []);

	// Request location on mount
	useEffect(() => {
		requestLocation();
	}, [requestLocation]);

	// Monitor permission state changes
	useEffect(() => {
		if (!navigator.permissions) return;

		let permissionStatus: PermissionStatus | null = null;

		const handlePermissionChange = () => {
			if (permissionStatus?.state === 'granted') {
				requestLocation();
			} else if (permissionStatus?.state === 'denied') {
				setState({
					...NO_FIX,
					loading: false,
					error: 'Location access denied. Please allow location access in your browser settings to proceed.',
				});
			}
		};

		navigator.permissions
			.query({ name: 'geolocation' })
			.then((status) => {
				permissionStatus = status;
				status.addEventListener('change', handlePermissionChange);
			})
			.catch(() => {
				// Permissions API not supported, silent fail
			});

		return () => {
			if (permissionStatus) {
				permissionStatus.removeEventListener('change', handlePermissionChange);
			}
		};
	}, [requestLocation]);

	return {
		...state,
		retry: requestLocation,
	};
};

export default useGeolocation;
